// src/components/assistant/WorkspaceWidgetCards.tsx
// Widget cards displayed in assistant responses for workspace mode

import React, { useState, useRef, useEffect } from "react";
import { Plus, RefreshCw, ChevronDown, LayoutDashboard, PlusCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDashboardStore } from "@/stores/dashboardStore";
import type { AIWidgetSpec } from "@/services/operationCenter/DashboardAIService";
import type { WidgetType } from "@/types/dashboard";

interface WidgetCardProps {
  widget: AIWidgetSpec;
  onAdd: () => void;
  addCount: number;
}

const WidgetCard: React.FC<WidgetCardProps> = ({ widget, onAdd, addCount }) => {
  const isAdded = addCount > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-xl border transition-colors ${
        isAdded
          ? "bg-emerald-500/10 border-emerald-500/30"
          : "bg-gray-800/50 border-gray-700/50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium uppercase tracking-wide">
              {widget.type.replace(/_/g, " ")}
            </span>
          </div>
          <h4 className="text-sm font-medium text-white mt-1.5 truncate">
            {widget.title}
          </h4>
        </div>
        <button
          onClick={onAdd}
          className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
        >
          {isAdded ? (
            <>
              <RefreshCw className="w-3 h-3" />
              Again ({addCount})
            </>
          ) : (
            <>
              <Plus className="w-3 h-3" />
              Add
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

interface WorkspaceWidgetCardsProps {
  widgets: AIWidgetSpec[];
  /** @deprecated No longer used - component uses dashboard store directly */
  onAddWidget?: (widget: AIWidgetSpec) => void;
  /** @deprecated No longer used - component uses dashboard store directly */
  onAddAll?: (() => void) | undefined;
}

const WorkspaceWidgetCards: React.FC<WorkspaceWidgetCardsProps> = ({
  widgets,
}) => {
  const [addCounts, setAddCounts] = useState<Map<number, number>>(new Map());
  const [showDashboardPicker, setShowDashboardPicker] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const newDashboardInputRef = useRef<HTMLInputElement>(null);

  // Get dashboards from store
  const dashboards = useDashboardStore((s) => s.dashboards);
  const activeDashboardId = useDashboardStore((s) => s.activeDashboardId);
  const addWidgetToDashboard = useDashboardStore((s) => s.addWidgetToDashboard);
  const createDashboard = useDashboardStore((s) => s.createDashboard);

  // Focus input when creating new dashboard
  useEffect(() => {
    if (isCreatingNew && newDashboardInputRef.current) {
      newDashboardInputRef.current.focus();
    }
  }, [isCreatingNew]);

  // Determine effective target dashboard
  const targetDashboardId = selectedDashboardId ?? activeDashboardId ?? dashboards[0]?.id;
  const targetDashboard = dashboards.find((d) => d.id === targetDashboardId);
  const hasNoDashboards = dashboards.length === 0;

  // Handle creating a new dashboard
  const handleCreateNewDashboard = () => {
    const name = newDashboardName.trim() || "My Workspace";
    const newDash = createDashboard(name);
    setSelectedDashboardId(newDash.id);
    setIsCreatingNew(false);
    setNewDashboardName("");
    setShowDashboardPicker(false);
  };

  const handleAdd = (widget: AIWidgetSpec, idx: number) => {
    let dashId = targetDashboardId;

    // Create a dashboard if none exist
    if (!dashId) {
      const newDash = createDashboard("My Workspace");
      dashId = newDash.id;
      setSelectedDashboardId(dashId);
    }

    addWidgetToDashboard(
      dashId,
      widget.type as WidgetType,
      widget.config as Record<string, unknown>,
      widget.title
    );
    setAddCounts((prev) => new Map(prev).set(idx, (prev.get(idx) ?? 0) + 1));
  };

  const handleAddAll = () => {
    let dashId = targetDashboardId;

    // Create a dashboard if none exist
    if (!dashId) {
      const newDash = createDashboard("My Workspace");
      dashId = newDash.id;
      setSelectedDashboardId(dashId);
    }

    widgets.forEach((widget) => {
      addWidgetToDashboard(
        dashId,
        widget.type as WidgetType,
        widget.config as Record<string, unknown>,
        widget.title
      );
    });

    setAddCounts((prev) => {
      const next = new Map(prev);
      widgets.forEach((_, idx) => {
        next.set(idx, (prev.get(idx) ?? 0) + 1);
      });
      return next;
    });
  };

  if (widgets.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {/* Dashboard selector - always shown */}
      <div className="relative mb-3">
        <button
          onClick={() => {
            setShowDashboardPicker(!showDashboardPicker);
            setIsCreatingNew(false);
          }}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-800/70 border border-gray-700/50 rounded-lg hover:bg-gray-800 transition-colors text-xs"
        >
          <div className="flex items-center gap-2 text-gray-300">
            <LayoutDashboard className="w-3.5 h-3.5 text-gray-500" />
            <span>Add to:</span>
            <span className="text-white font-medium">
              {hasNoDashboards ? "New workspace" : targetDashboard?.name ?? "Select dashboard"}
            </span>
          </div>
          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${showDashboardPicker ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {showDashboardPicker && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700/50 rounded-lg shadow-xl z-10 overflow-hidden"
            >
              {/* Existing dashboards */}
              {dashboards.map((dashboard) => (
                <button
                  key={dashboard.id}
                  onClick={() => {
                    setSelectedDashboardId(dashboard.id);
                    setShowDashboardPicker(false);
                    setIsCreatingNew(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-700/50 transition-colors ${
                    dashboard.id === targetDashboardId && !isCreatingNew
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "text-gray-300"
                  }`}
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  <span className="flex-1 truncate">{dashboard.name}</span>
                  {dashboard.id === activeDashboardId && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
                      Active
                    </span>
                  )}
                  {dashboard.is_default && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
                      Default
                    </span>
                  )}
                </button>
              ))}

              {/* Divider if there are existing dashboards */}
              {dashboards.length > 0 && (
                <div className="border-t border-gray-700/50 my-1" />
              )}

              {/* Create new dashboard option */}
              {isCreatingNew ? (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={newDashboardInputRef}
                      type="text"
                      value={newDashboardName}
                      onChange={(e) => setNewDashboardName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateNewDashboard();
                        if (e.key === "Escape") {
                          setIsCreatingNew(false);
                          setNewDashboardName("");
                        }
                      }}
                      placeholder="Dashboard name..."
                      className="flex-1 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                    />
                    <button
                      onClick={handleCreateNewDashboard}
                      className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs hover:bg-emerald-500/30 transition-colors"
                    >
                      Create
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsCreatingNew(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  <span>Create new dashboard</span>
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {widgets.map((widget, idx) => (
        <WidgetCard
          key={idx}
          widget={widget}
          onAdd={() => handleAdd(widget, idx)}
          addCount={addCounts.get(idx) ?? 0}
        />
      ))}

      {widgets.length > 1 && (
        <button
          onClick={handleAddAll}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-xl hover:bg-emerald-500/30 transition-colors text-xs font-medium"
        >
          <Plus className="w-3.5 h-3.5" />
          Add All Widgets
        </button>
      )}
    </div>
  );
};

export default WorkspaceWidgetCards;
